/* eslint-disable max-lines */
import { SummaryTracking } from "@/lib/tracking/summary";
import { Chart, Tile, ChartTypes, NumberFormatter, formatNumber } from "@uicore";
import { aiServicesLegendFormatter } from "./aiServicesLegendFormatter";
import { Card, Alert, Button, Stack, Typography } from "@altimateai/lego";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import classes from "../summary.module.scss";
import { Bar, TooltipProps } from "recharts";
import AggregateFilter from "./AggregateFilter";
import BreakdownSkeleton from "./BreakdownSkeleton";
import { TotalCostEntities, AggregateByValues, getBarColor } from "./constants";
import { getCostBreakdown } from "@/helpers/apis";
import {
  AggregatedComputeGraphResponse,
  AggregatedStorageGraphResponse,
  OverallCostSummaryResponse,
  TotalCosts,
} from "../context/types";
import { formatDate, getKey } from "./utils";
import AccordionTitle from "./AccordionTitle";
import FailureComponent from "./FailureComponent";
import CostChartTooltip from "./CostChartTooltip";
import dayjs from "dayjs";
import { useAppState } from "@/modules/app/useAppContext";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import SummaryChatButton from "./SummaryChatButton";
import CostChartSuggestions from "./CostChartSuggestions";
import { InfoIcon } from "@/assets/icons";
import { useSelectedInstance } from "../SummaryNew";
import { useServerlessOtherCategory } from "../useServerlessOtherCategory";
import { getCostTypeLabel } from "./labels";

export { getCostTypeLabel } from "./labels";

interface Props {
  isFuture?: boolean;
  currentStartDate: Date;
  currentEndDate: Date | null;
}

// Shape mirrors `TotalCostEntitiesBase.other.fields[number]` (the only
// shape the chart renderer + tooltip consume). Exported as a loose
// structural type so the derivation function can be unit-tested
// without instantiating TotalCostEntities.
export interface OtherInlineField {
  key: { current: string; estimate: string };
  label: string;
  isTotal?: boolean;
  hideSticker?: boolean;
  showChangePercentage?: boolean;
}

const RESERVED_KEYS = new Set(["date", "total", "total_estimate", "is_estimate", "currency"]);

/**
 * Build the Other-tab sub-bar field list from response rows.
 *
 * Unions numeric keys across **every** row (not just row 0) so a
 * usage_type that begins billing mid-window — absent or null on
 * day 1, populated on day 3 — still renders. The Zod schema's
 * `.catchall(z.number().nullable().optional())` explicitly allows
 * null values, hence the `typeof === "number"` guard which doubles
 * as a null-reject.
 *
 * Returns `null` when there are no rows OR no derived keys, so the
 * caller can fall back to the static `TotalCostEntitiesBase.other`
 * config.
 *
 * `showChangePercentage` is intentionally `false` for dynamic
 * series — the backend doesn't emit `percentage_<key>` for arbitrary
 * usage_types, so the per-row % delta would always render blank.
 *
 * Exported for unit testing — Recharts legends don't paint in jsdom,
 * so direct DOM assertions on bar names are unreliable.
 */
export function deriveOtherInlineFields(
  chartsData: ReadonlyArray<Record<string, unknown>> | null | undefined
): OtherInlineField[] | null {
  if (!chartsData || chartsData.length === 0) return null;

  const numericKeys = new Set<string>();
  for (const row of chartsData) {
    for (const [k, v] of Object.entries(row)) {
      if (RESERVED_KEYS.has(k)) continue;
      if (k.endsWith("_estimate")) continue;
      if (typeof v !== "number") continue;
      numericKeys.add(k);
    }
  }
  if (numericKeys.size === 0) return null;

  return [
    {
      key: { current: "total", estimate: "total_estimate" },
      label: "Other",
      isTotal: true,
      hideSticker: true,
    },
    ...Array.from(numericKeys)
      .sort()
      .map(
        (k): OtherInlineField => ({
          key: { current: k, estimate: `${k}_estimate` },
          label: getCostTypeLabel(k),
          showChangePercentage: false,
        })
      ),
  ];
}

const CostBreakdown = ({ isFuture, currentStartDate, currentEndDate }: Props) => {
  const { currency, userGroup, rbacEnabled, userGroupsLoaded, enableProjectionAgent } =
    useAppState();
  const [costType, setCostType] = useState("overall");
  const [aggregateBy, setAggregateBy] = useState(
    isFuture ? AggregateByValues.Monthly : AggregateByValues.Daily
  );
  const yearMode = aggregateBy === AggregateByValues.Monthly;
  const onAggregateSelect = (value: string) => {
    setAggregateBy(value);
  };

  const selectedInstance = useSelectedInstance();

  // Memoize so the deep-clone inside TotalCostEntities() doesn't run on
  // every render and bust downstream useMemo dep-array equality checks.
  const totalCostEntities = useMemo(
    () => TotalCostEntities(rbacEnabled, userGroup),
    [rbacEnabled, userGroup]
  );

  const getEndPoint = () => {
    if (isFuture) {
      return `${costType}/estimate`;
    }

    return costType;
  };

  const hasNoAccess = rbacEnabled && userGroup && !userGroup.is_owner;
  // Storage and Other are account-scoped (NULL warehouse_id); warehouse-RBAC
  // can't meaningfully filter them, so don't even fire the request — both
  // the chart and the response data would be hidden anyway.
  const isRbacBlocked = Boolean(hasNoAccess) && (costType === "storage" || costType === "other");

  const { isLoading, isFetching, data, error } = useQuery({
    queryKey: [
      `costBreakdown-${isFuture ? "future" : "past"}`,
      costType,
      currentStartDate?.getTime(),
      currentEndDate?.getTime(),
      aggregateBy,
      selectedInstance,
      userGroup?.id,
    ],
    queryFn: () =>
      getCostBreakdown<
        OverallCostSummaryResponse | AggregatedStorageGraphResponse | AggregatedComputeGraphResponse
      >(getEndPoint(), {
        start_date: getDateByStartOfDayWithoutTimeoffset(currentStartDate).toISOString(),
        end_date: currentEndDate
          ? getDateByEndOfDayWithoutTimeoffset(currentEndDate)?.toISOString()
          : undefined,
        aggregation_level: aggregateBy,
        ...(selectedInstance && selectedInstance !== "all" && { instance_id: selectedInstance }),
      }),
    enabled: Boolean(currentStartDate && currentEndDate && userGroupsLoaded) && !isRbacBlocked,
  });

  useEffect(() => {
    if (error) SummaryTracking.costBreakdownLoadFailed(error);
  }, [error]);

  const { data: totalCosts, error: totalCostsError } = useQuery({
    queryKey: [
      `totalcosts-${isFuture ? "future" : "past"}`,
      currentStartDate?.getTime(),
      currentEndDate?.getTime(),
      aggregateBy,
      selectedInstance,
      userGroup?.id,
    ],
    queryFn: () =>
      getCostBreakdown<TotalCosts>(isFuture ? `total/estimate` : "total", {
        start_date: getDateByStartOfDayWithoutTimeoffset(currentStartDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(currentEndDate)?.toISOString(),
        aggregation_level: aggregateBy,
        ...(selectedInstance && selectedInstance !== "all" && { instance_id: selectedInstance }),
      }),
    enabled: Boolean(currentStartDate && currentEndDate && userGroupsLoaded),
  });

  useEffect(() => {
    if (totalCostsError) SummaryTracking.costBreakdownLoadFailed(totalCostsError);
  }, [totalCostsError]);

  const rawChartsData = useMemo(() => {
    if (!isFuture) {
      return data?.graph;
    }
    // future cost graph has only monthly aggregation
    // For current month, we need to set projected amount to be difference so the graph bar will render properly
    return data?.graph.map(item => {
      const isCurrentMonth = dayjs().isSame(item.date, "month");
      if (!isCurrentMonth) {
        return item;
      }

      const fields = totalCostEntities[costType as keyof typeof totalCostEntities]?.fields || [];

      return {
        ...item,
        ...fields.reduce((acc: Partial<typeof item>, f) => {
          const keys = f.key;
          const row = item as unknown as Record<string, number | null | undefined>;
          const est = row[keys.estimate];
          // Skip when the backend didn't return an estimate for this category
          // (Optional fields default to undefined and would yield NaN here).
          if (est == null) return acc;
          const cur = row[keys.current] ?? 0;
          // @ts-expect-error valid keys
          acc[keys.estimate] = est - cur;
          return acc;
        }, {}),
      };
    });
  }, [isFuture, data?.graph, costType, totalCostEntities]);

  const chartsData = useServerlessOtherCategory(
    rawChartsData as unknown as Record<string, number | null | undefined>[] | undefined,
    costType,
    totalCostEntities
  );

  // Filter out fields with hideWhenZero when their total value is 0 or undefined
  // Also filter out "other" cost type in future state
  const filteredOverallFields = useMemo(() => {
    return totalCostEntities.overall.fields.filter(field => {
      if (isFuture && field.label === "other") {
        return false;
      }
      if (!field.hideWhenZero) {
        return true;
      }
      // Check if the total value for this field is non-zero
      const fieldKey = field.key.current;
      const value = totalCosts?.[fieldKey as keyof typeof totalCosts];
      return value !== undefined && value !== null && value !== 0;
    });
  }, [totalCostEntities.overall.fields, totalCosts, isFuture]);

  type OtherField = NonNullable<typeof totalCostEntities.other>["fields"][number];
  const otherInlineFields: OtherField[] | null = useMemo(
    () => (costType !== "other" ? null : deriveOtherInlineFields(chartsData)),
    [costType, chartsData]
  );

  const costBar = (props: {
    d: (typeof totalCostEntities.overall.fields)[0];
    barSize: number;
    selectedLegend: string | null | undefined;
    isFuture: boolean;
    data?: Record<string, unknown>[];
    isLast: boolean;
  }) => {
    const { d, barSize, selectedLegend } = props;
    const currentKey = getKey(d, false);
    const futureKey = getKey(d, true);

    // Since estimate legend has different name, we need to parse the string and modify to have estimate key
    const legend = selectedLegend?.includes(" (projected)")
      ? `${selectedLegend.replace(" (projected)", "")}_estimate`
      : selectedLegend;

    return (
      <>
        <Bar
          key={currentKey}
          barSize={barSize}
          name={d.label}
          hide={Boolean(legend && legend !== currentKey)}
          dataKey={currentKey}
          fill={getBarColor(currentKey)}
          stackId="single"
          radius={!isFuture && props.isLast ? [7, 7, 0, 0] : [0, 0, 0, 0]}
        />
        {/* show predictions only for future state */}
        {isFuture ? (
          <Bar
            key={futureKey}
            barSize={barSize}
            name={`${d.label} (projected)`}
            hide={Boolean(legend && legend !== futureKey)}
            dataKey={futureKey}
            fill={getBarColor(futureKey)}
            stackId="single"
            radius={[3, 3, 0, 0]}
          />
        ) : null}
      </>
    );
  };

  if (isLoading) {
    return <BreakdownSkeleton type="section" />;
  }

  return (
    <Card
      className={classes.section_card}
      id={isFuture ? "tour-summary-cost-future" : "tour-summary-cost"}
    >
      <AccordionTitle
        title={<Typography variant="section-heading">Costs</Typography>}
        howToText="How to read the charts"
        howToContent={
          <p>
            {isFuture
              ? "These charts show the cost data for this year - split between already occurred costs and projected costs. If you hover over, you will see change % and breakdown no.s (if applicable). Don't miss the DataPilot icon in the top right corner of the chart to start talking with the data!"
              : "These charts show the cost data for the period specified for Current state. Charts can aggregate data at daily, weekly or monthly level. If you hover over, you will see change % and breakdown no.s (if applicable). Don't miss the DataPilot icon in the top right corner of the chart to start talking with the data!"}
          </p>
        }
      />
      <Stack>
        {error ? (
          <FailureComponent title={"Failed to load cost breakdown. Please try again later."} />
        ) : (
          <>
            <div className={classes.tileWrap}>
              <Tile
                color="#FF754C"
                title="Total Costs"
                helpText={
                  isFuture
                    ? "Projected total Snowflake costs during the year for all teams combined"
                    : `Snowflake costs during the period selected: ${formatDate(
                        currentStartDate,
                        yearMode
                      )} to ${formatDate(currentEndDate, yearMode)}`
                }
                value={<NumberFormatter value={totalCosts?.total} options={{ currency }} />}
              >
                <Stack direction="column">
                  <Tile
                    title="Compute"
                    value={<NumberFormatter value={totalCosts?.compute} options={{ currency }} />}
                  />
                  <Tile
                    title="Storage"
                    value={<NumberFormatter value={totalCosts?.storage} options={{ currency }} />}
                  />
                  <Tile
                    title="Serverless"
                    value={
                      <NumberFormatter value={totalCosts?.serverless} options={{ currency }} />
                    }
                  />
                  <Tile
                    title="Cloud Services"
                    value={
                      <NumberFormatter value={totalCosts?.cloud_services} options={{ currency }} />
                    }
                  />
                  <Tile
                    title="AI Services"
                    value={
                      <NumberFormatter value={totalCosts?.ai_services} options={{ currency }} />
                    }
                  />
                </Stack>
              </Tile>
            </div>
            <div className={classes.graphs}>
              <Stack className="al-justify-between">
                <Stack className="al-bg-blue-100 al-rounded-md !al-flex-wrap" gap="none">
                  {filteredOverallFields.map(d => {
                    const tabKey = d.tabKey || getKey(d, false);
                    return (
                      <Button
                        variant={costType === tabKey ? "default" : "ghost"}
                        key={tabKey}
                        className="al-capitalize"
                        onClick={() => setCostType(tabKey)}
                      >
                        {getCostTypeLabel(d.label)}
                      </Button>
                    );
                  })}
                </Stack>
                <Stack className="al-items-center">
                  <AggregateFilter
                    onSelect={onAggregateSelect}
                    aggregateBy={aggregateBy}
                    isFuture={isFuture}
                    startDate={currentStartDate}
                    endDate={currentEndDate}
                  />
                  {!isFuture && (
                    <SummaryChatButton
                      chartType={"current"}
                      aggregation={aggregateBy}
                      analysisType="cost"
                      startDate={currentStartDate}
                      endDate={currentEndDate}
                      costType={costType}
                    />
                  )}
                </Stack>
              </Stack>
              <div style={{ width: "100%", height: 500, padding: "1rem" }}>
                {isRbacBlocked ? (
                  // Account-scoped surfaces (Storage, Other) with NULL
                  // warehouse_id can't be meaningfully filtered by
                  // warehouse-scoped RBAC. Show the banner INSTEAD of the
                  // chart — and the useQuery above is already gated so no
                  // data was fetched.
                  <Alert color="danger" className="border-0">
                    <InfoIcon className="me-2" />
                    You don&apos;t have permission to access all this information. Change the team
                    to view the data.
                  </Alert>
                ) : isFetching ? (
                  <BreakdownSkeleton type="chart" />
                ) : (
                  <Chart
                    title="Cost Breakdown"
                    xAxisDataKey="date"
                    yAxisLabel={{
                      value: "Cost",
                    }}
                    xAxisLabelFormatter={value => `${formatDate(value, yearMode)}`}
                    yAxisLabelFormatter={value =>
                      `${formatNumber(value, {
                        currency,
                        maximumFractionDigits: 2,
                      })}`
                    }
                    type={ChartTypes.BarChart}
                    data={chartsData}
                    tooltipProps={{
                      content: (props: TooltipProps<number, string>) => (
                        <CostChartTooltip
                          {...props}
                          timeRange={aggregateBy as "day" | "week" | "month"}
                          isFuture={isFuture}
                          type={costType}
                          totalCostEntities={totalCostEntities}
                          fields={otherInlineFields ?? undefined}
                          colorFor={getBarColor}
                        />
                      ),
                    }}
                    chartProps={
                      costType === "cloud_services" && !isFuture ? { stackOffset: "sign" } : {}
                    }
                    legendProps={{ formatter: aiServicesLegendFormatter }}
                  >
                    {({ barSize, selectedLegend }) => {
                      const currentCostTypeFields =
                        otherInlineFields ??
                        totalCostEntities[costType as keyof typeof totalCostEntities]?.fields ??
                        [];
                      const allFields = isFuture
                        ? [currentCostTypeFields.find(d => d.isTotal)]
                        : currentCostTypeFields.filter(d => !d.isTotal);
                      // Skip stacked-bar segments for hideWhenZero fields
                      // that never carry a value in the visible range. Keeps
                      // the stack height aligned with visible tooltip rows.
                      const fields = allFields?.filter(d => {
                        if (!d?.hideWhenZero) return true;
                        const key = isFuture ? d.key.estimate : d.key.current;
                        return chartsData?.some(row => {
                          const v = (row as Record<string, unknown>)[key];
                          return typeof v === "number" && v !== 0;
                        });
                      });
                      return (
                        <>
                          {fields?.map((d, index) => {
                            if (!d) {
                              return null;
                            }
                            return (
                              <>
                                {costBar({
                                  d,
                                  barSize,
                                  selectedLegend,
                                  isFuture: Boolean(isFuture),
                                  data: chartsData,
                                  isLast: index === fields.length - 1,
                                })}
                              </>
                            );
                          })}
                        </>
                      );
                    }}
                  </Chart>
                )}
              </div>
              {!isFuture && enableProjectionAgent && (
                <CostChartSuggestions
                  costType={costType}
                  startDate={currentStartDate}
                  endDate={currentEndDate}
                  aggregation={aggregateBy}
                />
              )}
            </div>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default CostBreakdown;
